import * as React from 'react';
import { Typography, List, ListItem, ListItemText, ListItemButton, Box, Stack, Button, Divider } from '@mui/material';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useDeleteInvoicePayment } from '../../../hooks/invoice/useDeleteInvoicePayment';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import InvoicePaymentForm from '../forms/InvoicePaymentForm';
import InvoicePaymentView from '../modals/InvoicePaymentView';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InvoicePaymentsList({ invoice, onSaveSuccess, onDeleteSuccess }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const grandTotal = invoice.payments.reduce((sum, payment) => sum + parseFloat(payment.amount_paid), 0);

  const [paymentViewOpen, setPaymentViewOpen] = React.useState(false);
  const [paymentFormOpen, setPaymentFormOpen] = React.useState(false);
  const [paymentFormMode, setPaymentFormMode] = React.useState('create');
  const [selectedPayment, setSelectedPayment] = React.useState(null);

  const handleAddPayment = () => {
    setSelectedPayment(null);
    setPaymentFormMode('create');
    setPaymentFormOpen(true);
  };

  const handleListItemClick = (payment) => {
    setSelectedPayment(payment);
    setPaymentViewOpen(true);
  };

  const handleListItemEdit = (payment) => {
    setSelectedPayment(payment);
    setPaymentFormMode('edit');
    setPaymentFormOpen(true);
  };

  const onDeletePaymentSuccess = () => {
    onDeleteSuccess(invoice);
    showSnackbar(t('invoice.payments.messages.delete_success'), "success");
  };

  const { remove } = useDeleteInvoicePayment(onDeletePaymentSuccess);

  const handleListItemDelete = async (payment) => {
    try {
      await remove(payment.id);
    } catch (err) {
      showSnackbar(t('invoice.payments.messages.delete_fail'), "error");
    }
  };
  
  return (
    <>
    <List>
    {invoice.payments.map((payment) => (
      <React.Fragment key={"payment" + payment.id}>
      <ListItem 
        sx={{ py: 1 }}
        alignItems="flex-start"
        secondaryAction={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VerticalHamburgerMenu
              options={[
                  { 
                      id: 1, 
                      name: 'View',
                      callback: () => handleListItemClick(payment),
                      disabled: !userHasAccess(SUB_MODULES.INVOICE, 1)
                  },
                  { 
                      id: 2, 
                      name: 'Edit',
                      callback: () => handleListItemEdit(payment),
                      disabled: !userHasAccess(SUB_MODULES.INVOICE, 2)
                  },
                  { 
                    id: 3, 
                    name: 'Delete',
                    callback: () => handleListItemDelete(payment),
                    confirmMessage: t('invoice.payments.messages.delete_confirm', { name: payment.amount_paid }),
                    disabled: !userHasAccess(SUB_MODULES.INVOICE, 2)
                  }
              ]}
              />
          </Box>
        }
      >
        <ListItemText
          onClick={() => handleListItemClick(payment)}
          slotProps={{ 
            primary: { component: "div" }, 
            secondary: { component: "div" },  
          }}
          primary={
            <>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                  maxWidth: '85%',
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
              <Typography component="span" variant="body2">
                {`${payment.date}`}
              </Typography>
              <Typography component="span" variant="body1">
                {`${numberToCurrencyFormat(payment.amount_paid)}`}
              </Typography>
            </Stack>
            </>
          }
          secondary={
            <>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  maxWidth: '85%',
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="body2">
                  {`${payment.paymentMethod.name}`}
                </Typography>
              </Stack>
            </>
          }
        />
      </ListItem>
      <Divider />
      </React.Fragment>
    ))}
    </List>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: 2,
          maxWidth: '85%',
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
      <Typography component="span" variant="body2">
        Total Paid
      </Typography>
      <Typography component="span" variant="body1">
        {`${numberToCurrencyFormat(grandTotal.toFixed(2))}`}
      </Typography>
    </Stack>
    <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: 2,
          maxWidth: '85%',
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
      <Typography component="span" variant="body2">
        Balance
      </Typography>
      <Typography component="span" variant="body1">
        {`${numberToCurrencyFormat(invoice.calculated_balance_remaining)}`}
      </Typography>
    </Stack>
    <br />

    <RequirePermission subModuleName={SUB_MODULES.INVOICE} minAccessLevel={2}>
      <Button 
        disabled={Number(invoice.calculated_balance_remaining) === 0}
        onClick={handleAddPayment} 
        variant='outlined' 
        fullWidth
      >
        Add Payment
      </Button>
    </RequirePermission>

    <InvoicePaymentForm
      invoice={invoice}
      mode={paymentFormMode}
      payment={selectedPayment}
      onSaveSuccess={onSaveSuccess}
      open={paymentFormOpen}
      onClose={() => setPaymentFormOpen(false)}
    />

    <InvoicePaymentView
      handleEdit={handleListItemEdit}
      payment={selectedPayment}
      invoice={invoice}
      open={paymentViewOpen}
      onClose={() => setPaymentViewOpen(false)}
    />
    </>
  );
}